import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export type ApiParams = Record<string, string | number | boolean>;

/**
 * Zentraler Zugang zum Backend: setzt die Basis-URL und `withCredentials` an einer Stelle,
 * damit kein Endpunkt sie versehentlich vergisst.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiURL;

  get<T>(path: string, params?: ApiParams): Observable<T> {
    return this.http.get<T>(this.url(path), { withCredentials: true, params: this.toHttpParams(params) });
  }

  post<T>(path: string, body?: unknown, params?: ApiParams): Observable<T> {
    return this.http.post<T>(this.url(path), body ?? null, {
      withCredentials: true,
      params: this.toHttpParams(params),
    });
  }

  put<T>(path: string, body?: unknown): Observable<T> {
    return this.http.put<T>(this.url(path), body ?? null, { withCredentials: true });
  }

  delete<T>(path: string): Observable<T> {
    return this.http.delete<T>(this.url(path), { withCredentials: true });
  }

  getBlob(path: string): Observable<Blob> {
    return this.http.get(this.url(path), { withCredentials: true, responseType: 'blob' });
  }

  postBlob(path: string, body?: unknown): Observable<Blob> {
    return this.http.post(this.url(path), body ?? null, { withCredentials: true, responseType: 'blob' });
  }

  getText(path: string): Observable<string> {
    return this.http.get(this.url(path), { withCredentials: true, responseType: 'text' });
  }

  /** `contentType` setzen, wenn der Body bereits serialisiert ist (sonst schickt Angular text/plain). */
  postText(path: string, body?: unknown, contentType?: string): Observable<string> {
    return this.http.post(this.url(path), body ?? null, {
      withCredentials: true,
      responseType: 'text',
      headers: contentType ? { 'Content-Type': contentType } : undefined,
    });
  }

  private url(path: string): string {
    return `${this.baseUrl}/${path.replace(/^\//, '')}`;
  }

  private toHttpParams(params?: ApiParams): HttpParams | undefined {
    if (!params) {
      return undefined;
    }
    return Object.entries(params)
      .filter(([, value]) => value !== null && value !== undefined)
      .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
  }
}
